import type { AuthResponse } from './authApi';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8080/api/v1';

export interface SalonInfo {
    id: string;
    name: string;
    address?: string;
    isActive: boolean;
    subscriptionPlan: 'FREE' | 'PRO' | 'ENTERPRISE';
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    slug?: string;
    publicBookingEnabled?: boolean;
}

export interface SalonUpdateRequest {
    name?: string;
    address?: string;
    logoUrl?: string;
    primaryColor?: string;
    secondaryColor?: string;
    slug?: string;
    publicBookingEnabled?: boolean;
}

export interface UploadUrlRequest {
    fileName: string;
    contentType: string;
    salonId: string;
    folder: string; // 'logos', 'employees', etc.
}

export interface UploadUrlResponse {
    uploadUrl: string;
    publicUrl: string;
    fileKey: string;
}

const getAuthToken = (): string | null => {
    return localStorage.getItem('token');
};

const getCurrentUser = (): AuthResponse | null => {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
};

export const salonApi = {
    /**
     * Get all salons - only for SUPER_ADMIN users
     */
    getAllSalons: async (): Promise<SalonInfo[]> => {
        const token = getAuthToken();
        const user = getCurrentUser();

        if (!token || user?.role !== 'SUPER_ADMIN') {
            throw new Error('Unauthorized: Only SUPER_ADMIN can view all salons');
        }

        const response = await fetch(`${API_URL}/salons`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error('Failed to fetch salons');
        }

        return response.json();
    },

    /**
     * Get salon by ID
     */
    getSalonById: async (salonId: string): Promise<SalonInfo> => {
        const token = getAuthToken();

        const response = await fetch(`${API_URL}/salons/${salonId}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error('Failed to fetch salon');
        }

        return response.json();
    },

    /**
     * Update salon details including branding
     */
    updateSalon: async (salonId: string, data: SalonUpdateRequest): Promise<SalonInfo> => {
        const token = getAuthToken();

        const response = await fetch(`${API_URL}/salons/${salonId}`, {
            method: 'PUT',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(data),
        });

        if (!response.ok) {
            throw new Error('Failed to update salon');
        }

        return response.json();
    },

    /**
     * Get presigned URL for uploading files to R2
     */
    getUploadUrl: async (request: UploadUrlRequest): Promise<UploadUrlResponse> => {
        const token = getAuthToken();

        const response = await fetch(`${API_URL}/storage/upload-url`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify(request),
        });

        if (!response.ok) {
            throw new Error('Failed to get upload URL');
        }

        return response.json();
    },

    /**
     * Upload file directly to Cloudinary or R2 using presigned URL
     */
    uploadFile: async (uploadUrl: string, file: File): Promise<boolean> => {
        // Log the upload attempt for better diagnostics
        console.log('[salonApi] Starting upload to:', uploadUrl);

        // Detect if we are uploading to Cloudinary (which requires POST and FormData)
        // We trim and check case-insensitively for maximum reliability
        const normalizedUrl = uploadUrl.trim().toLowerCase();
        const isCloudinary = normalizedUrl.includes('cloudinary') || normalizedUrl.includes('signature=');

        if (isCloudinary) {
            console.log('[salonApi] Detected Cloudinary. Performing POST (signed upload)...');
            try {
                const formData = new FormData();
                formData.append('file', file);

                // Transfer all query parameters from the URL to FormData fields
                const targetUrl = new URL(uploadUrl);
                targetUrl.searchParams.forEach((value, key) => {
                    // console.log(`[salonApi] Adding FormData parameter: ${key}=${value}`);
                    formData.append(key, value);
                });

                // Clear query params from the URL as Cloudinary expects them in the Form body for signed uploads
                targetUrl.search = '';

                const response = await fetch(targetUrl.toString(), {
                    method: 'POST',
                    body: formData,
                });

                if (!response.ok) {
                    const errorText = await response.text().catch(() => 'Unknown error');
                    console.error('[salonApi] Cloudinary upload error details:', errorText);
                }

                return response.ok;
            } catch (err) {
                console.error('[salonApi] Failed to upload to Cloudinary:', err);
                return false;
            }
        }

        // Standard R2/S3 behavior (PUT with raw file body)
        console.log('[salonApi] Detected Standard S3/R2 storage. Performing PUT...');
        const response = await fetch(uploadUrl, {
            method: 'PUT',
            headers: {
                'Content-Type': file.type,
            },
            body: file,
        });

        if (!response.ok) {
            console.error('[salonApi] Standard storage upload error:', response.status, response.statusText);
        }

        return response.ok;
    },

    /**
     * Get presigned URL for downloading/viewing private files from R2
     */
    getDownloadUrl: async (fileKey: string): Promise<string> => {
        const token = getAuthToken();

        // Encode fileKey to handle slashes correctly in URL params
        const encodedKey = encodeURIComponent(fileKey);

        const response = await fetch(`${API_URL}/storage/download-url?fileKey=${encodedKey}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
            },
        });

        if (!response.ok) {
            throw new Error('Failed to get download URL');
        }

        const data = await response.json();
        return data.url;
    }
};
